import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { messageService, userProfileService } from '../../services/supabaseService';

const Communication = () => {
  const { user, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const isTrainer = userProfile?.role === 'personal_trainer';

  useEffect(() => {
    loadContacts();
  }, [user, userProfile]);

  useEffect(() => {
    if (selectedContact) {
      loadMessages();
      const unsubscribe = messageService?.subscribeToMessages(user?.id, (newMsg) => {
        if (newMsg?.senderId === selectedContact?.id) {
          setMessages(prev => [...prev, newMsg]);
        }
      });
      return () => unsubscribe();
    }
  }, [selectedContact, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadContacts = async () => {
    setIsLoading(true);
    try {
      if (isTrainer) {
        const { data } = await userProfileService?.getTrainerClients(user?.id);
        setContacts(data?.map(a => a?.client) || []);
        if (data && data?.length > 0) {
          setSelectedContact(data?.[0]?.client);
        }
      } else {
        const { data } = await userProfileService?.getClientTrainer(user?.id);
        if (data?.trainer) {
          setContacts([data?.trainer]);
          setSelectedContact(data?.trainer);
        }
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedContact) return;
    try {
      const { data } = await messageService?.getConversation(user?.id, selectedContact?.id);
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || !selectedContact) return;

    const { data, error } = await messageService?.sendMessage(selectedContact?.id, newMessage);
    if (!error && data) {
      setMessages(prev => [...prev, data]);
      setNewMessage('');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Communication - Form Forge</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Communication</h1>
            <p className="text-muted-foreground mt-2">
              {isTrainer ? 'Message your clients' : 'Message your trainer'}
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6 h-[calc(100vh-250px)]">
              <div className="col-span-12 md:col-span-4 bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Users" size={20} />
                    {isTrainer ? 'Clients' : 'Trainer'}
                  </h2>
                </div>
                <div className="overflow-y-auto h-full">
                  {contacts?.length > 0 ? (
                    contacts?.map((contact) => (
                      <button
                        key={contact?.id}
                        onClick={() => setSelectedContact(contact)}
                        className={`w-full p-4 flex items-center gap-3 hover:bg-muted transition-colors ${
                          selectedContact?.id === contact?.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                          {contact?.fullName?.charAt(0)}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-foreground">{contact?.fullName}</p>
                          <p className="text-sm text-muted-foreground truncate">{contact?.email}</p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <Icon name="Users" size={48} className="mx-auto mb-4" />
                      <p>No contacts available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-12 md:col-span-8 bg-card border border-border rounded-xl flex flex-col">
                {selectedContact ? (
                  <>
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                          {selectedContact?.fullName?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{selectedContact?.fullName}</p>
                          <p className="text-sm text-muted-foreground">{selectedContact?.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Icon name="Phone" size={18} />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Icon name="Video" size={18} />
                        </Button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages?.length > 0 ? (
                        messages?.map((message) => {
                          const isSent = message?.senderId === user?.id;
                          return (
                            <div
                              key={message?.id}
                              className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[70%] rounded-lg p-3 ${
                                  isSent
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-foreground'
                                }`}
                              >
                                <p className="text-sm">{message?.content}</p>
                                <p className={`text-xs mt-1 ${
                                  isSent ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                }`}>
                                  {format(new Date(message?.createdAt), 'HH:mm')}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                          <div className="text-center">
                            <Icon name="MessageSquare" size={48} className="mx-auto mb-4" />
                            <p>No messages yet. Start the conversation!</p>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e?.target?.value)}
                          placeholder="Type a message..."
                          className="flex-1 px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <Button type="submit" disabled={!newMessage?.trim()}>
                          <Icon name="Send" size={18} />
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <Icon name="MessageSquare" size={64} className="mx-auto mb-4" />
                      <p>Select a contact to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Communication;